import { useEffect, useState } from 'react';
import { Lock, Mail, AlertTriangle, User, Sparkles, HelpCircle, X } from 'lucide-react';
import { adminLogin, adminInitialSetup, checkSetupRequired, type AdminSession } from './auth';
import { useEscapeToClose } from '../hooks/useEscapeToClose';

export function LoginScreen({ onLogin }: { onLogin: (s: AdminSession) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [setupRequired, setSetupRequired] = useState<boolean | null>(null);
  const [showForgotHelp, setShowForgotHelp] = useState(false);

  useEffect(() => {
    checkSetupRequired().then(setSetupRequired);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const session = await adminLogin(email, password);
      onLogin(session);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Pogrešan email ili lozinka');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Lozinke se ne poklapaju');
      return;
    }
    if (password.length < 6) {
      setError('Lozinka mora imati najmanje 6 karaktera');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const session = await adminInitialSetup(email, fullName, password);
      onLogin(session);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri kreiranju naloga');
    } finally {
      setLoading(false);
    }
  };

  // Loading initial state
  if (setupRequired === null) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
        Učitavanje...
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{
        width: '100%', maxWidth: 420,
        background: 'var(--color-bg-surface)',
        borderRadius: 12,
        padding: 32,
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 12,
            background: setupRequired ? '#10b981' : 'var(--color-primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            {setupRequired ? <Sparkles size={26} color="#fff" /> : <Lock size={26} color="#fff" />}
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            {setupRequired ? 'Dobrodošli u Efikasa' : 'Efikasa Admin'}
          </h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            {setupRequired ? 'Početni setup - kreiraj prvi sysadmin nalog' : 'System administrator panel'}
          </div>
        </div>

        {setupRequired ? (
          <form onSubmit={handleSetup} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field icon={User} label="Puno ime">
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoFocus
                required
                placeholder="Marko Markovic"
                style={inputStyle}
              />
            </Field>
            <Field icon={Mail} label="Email">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="admin@example.com"
                style={inputStyle}
              />
            </Field>
            <Field icon={Lock} label="Lozinka (min 6 karaktera)">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                style={inputStyle}
              />
            </Field>
            <Field icon={Lock} label="Potvrdi lozinku">
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                style={inputStyle}
              />
            </Field>

            {error && (
              <div style={errorBox}>
                <AlertTriangle size={14} /> {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email || !password || !fullName || !confirmPassword}
              style={{
                ...primaryBtn,
                background: '#10b981',
                opacity: loading || !email || !password || !fullName || !confirmPassword ? 0.5 : 1,
              }}
            >
              {loading ? 'Čuvanje...' : 'Sačuvaj prvi nalog'}
            </button>

            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textAlign: 'center', marginTop: 4, padding: 10, background: 'var(--color-bg)', borderRadius: 6 }}>
              Ovaj nalog dobija puna admin prava. Setup wizard radi samo dok u bazi nema nijednog aktivnog sysadmin-a.
            </div>
          </form>
        ) : (
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field icon={Mail} label="Email">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
                placeholder="admin@invoiceos.local"
                style={inputStyle}
              />
            </Field>
            <Field icon={Lock} label="Lozinka">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={inputStyle}
              />
            </Field>

            {error && (
              <div style={errorBox}>
                <AlertTriangle size={14} /> {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email || !password}
              style={{
                ...primaryBtn,
                background: loading || !email || !password ? 'var(--color-bg-hover)' : 'var(--color-primary)',
              }}
            >
              {loading ? 'Prijavljivanje...' : 'Prijavi se'}
            </button>

            <button
              type="button"
              onClick={() => setShowForgotHelp(true)}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--color-text-muted)', fontSize: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                padding: '4px 8px', marginTop: 4,
              }}
            >
              <HelpCircle size={12} /> Zaboravljena lozinka?
            </button>
          </form>
        )}

        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--color-border)', fontSize: 11, color: 'var(--color-text-muted)', textAlign: 'center' }}>
          Pristup samo za administratorske naloge
        </div>
      </div>

      {showForgotHelp && <ForgotPasswordHelp onClose={() => setShowForgotHelp(false)} />}
    </div>
  );
}

function ForgotPasswordHelp({ onClose }: { onClose: () => void }) {
  useEscapeToClose(onClose);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: 480,
        background: 'var(--color-bg-surface)',
        borderRadius: 12, padding: 0,
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        border: '1px solid var(--color-border)',
      }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--color-text-strong)' }}>Zaboravljena lozinka</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 }}>
            <X size={16} />
          </button>
        </header>
        <div style={{ padding: 24, fontSize: 13, color: 'var(--color-text)', lineHeight: 1.6 }}>
          <p style={{ margin: '0 0 14px' }}>
            Email-based password reset još uvek nije implementiran. Za sada postoje dve opcije:
          </p>
          <div style={{ padding: 12, background: 'var(--color-bg)', borderRadius: 6, marginBottom: 12 }}>
            <strong style={{ display: 'block', marginBottom: 4, color: 'var(--color-text-strong)' }}>1. Drugi sysadmin može da resetuje lozinku</strong>
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
              Ako postoji još jedan aktivan admin, on može otići na <code style={code}>Admin korisnici</code> → izabrati vaš nalog → Izmeni → uneti novu lozinku.
            </span>
          </div>
          {/*
            Ovde je ranije stajao SQL upit sa imenom tabele i kolone za lozinku.
            Ovo je ekran za PRIJAVU - vidi ga i neko ko nije prijavljen, pa je to
            bilo odavanje strukture baze nekome kome ionako ne koristi: iz
            pregledaca se SQL ne pokrece.
          */}
          <div style={{ padding: 12, background: 'var(--color-bg)', borderRadius: 6 }}>
            <strong style={{ display: 'block', marginBottom: 4, color: 'var(--color-text-strong)' }}>2. Server administrator</strong>
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
              Ako nema drugog aktivnog admina, obratite se osobi koja održava server - ona može da postavi novu lozinku.
            </span>
          </div>
          <button onClick={onClose} style={{ ...primaryBtn, marginTop: 16, background: 'var(--color-primary)' }}>
            U redu
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, children }: { icon: any; label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '0 12px',
        background: 'var(--color-bg)',
        borderRadius: 8,
        border: '1px solid var(--color-border)',
      }}>
        <Icon size={14} color="var(--color-text-muted)" />
        {children}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  flex: 1,
  border: 'none',
  background: 'transparent',
  color: 'var(--color-text-strong)',
  padding: '10px 0',
  outline: 'none',
  fontSize: 14,
};

const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '10px 12px', borderRadius: 8,
  background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
  fontSize: 13,
};

const primaryBtn: React.CSSProperties = {
  padding: '12px 16px', borderRadius: 8, border: 'none',
  color: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer',
  marginTop: 6,
};

const code: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 11,
  padding: '1px 6px',
  background: 'var(--color-bg)',
  borderRadius: 4,
};
